import { useDiagramData } from '@/hooks/useDiagramData';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useProject } from '@/contexts/ProjectContext';
import { READ_ONLY_WRITE_BACK_MESSAGE, STANDALONE_WRITE_BACK_MESSAGE } from '@/lib/projectVersionState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, CheckCircle2, XCircle, Pencil } from 'lucide-react';
import ReviewCommentsCallout from '@/components/review/ReviewCommentsCallout';
import ConnectModal from '@/components/threatcat/ConnectModal';
import EditEntityDialog from './entities/EditEntityDialog';
import { threatcatService } from '@/services/threatcatService';
import { draftDiagramService, type DraftDiagramOut } from '@/services/draftDiagramService';
import { ThreatCatAuthError } from '@/lib/errors';
import { entityTypeLabels, entityTypeColors } from '@/lib/constants';
import { entityLabel } from '@/lib/diagramFields';
import type { Boundary, Entity } from '@/types/diagram';

interface EntitiesTabProps {
  draftId?: number;
  onDraftUpdated?: (draft: DraftDiagramOut) => void;
}

export default function EntitiesTab({ draftId, onDraftUpdated }: EntitiesTabProps = {}) {
  const isDraft = draftId != null;
  const { currentDiagram, getEntityBoundaryChain, scopedEntities } = useDiagramData();
  const { diagrams, currentDiagramIndex, setAppState } = useApp();
  const { cookie, getCookie, disconnect } = useThreatCat();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const reviewIndex = useReviewCommentsIndex();
  const { projectId, detail } = useProject();
  const isStandaloneDiagram = !isDraft && projectId == null;
  const isReadOnly = detail?.permission === 'read_only';
  const isEntityWriteLocked = !isDraft && isWriteBackLocked;
  const isEditDisabled = isStandaloneDiagram || isReadOnly || isEntityWriteLocked;
  const editDisabledReason = isStandaloneDiagram
    ? STANDALONE_WRITE_BACK_MESSAGE
    : isReadOnly
      ? READ_ONLY_WRITE_BACK_MESSAGE
      : isEntityWriteLocked
        ? writeBackLockMessage
        : null;
  const [copied, setCopied] = useState(false);

  // ── Edit entity state ────────────────────────────────────────────────────────
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const pendingSaveEntityRef = useRef<Entity | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => clearCloseTimer, []);

  if (!currentDiagram) return null;

  const copyScopeTable = () => {
    let markdown = '| Systems in Scope | Systems out of scope |\n';
    markdown += '|-------------------|----------------------|\n';
    
    const maxRows = Math.max(scopedEntities.inScope.length, scopedEntities.outOfScope.length);
    for (let i = 0; i < maxRows; i++) {
      const inScopeItem = scopedEntities.inScope[i] || '';
      const outOfScopeItem = scopedEntities.outOfScope[i] || '';
      markdown += `| ${inScopeItem} | ${outOfScopeItem} |\n`;
    }
    
    navigator.clipboard.writeText(markdown);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2500);
  };

  const getBoundaryLabel = (boundary: Boundary) => getEntityBoundaryChain(boundary.id) || boundary.name;

  const openEditDialog = (entity: Entity) => {
    if (isEditDisabled) return;
    clearCloseTimer();
    setEditingEntity(entity);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const closeEditDialog = () => {
    clearCloseTimer();
    setEditingEntity(null);
    pendingSaveEntityRef.current = null;
  };

  const replaceEntity = (entities: Entity[], updated: Entity) =>
    entities.map((e) => (e.id === updated.id ? updated : e));

  const executeSaveEntity = async (updated: Entity, activeCookie?: string) => {
    if (isEditDisabled) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      if (isDraft) {
        const updatedEntities = replaceEntity(currentDiagram.entities, updated);
        const updatedDraft = await draftDiagramService.patchPayload(draftId, { entities: updatedEntities });
        setAppState({ currentDiagram: { ...currentDiagram, entities: updatedEntities } });
        onDraftUpdated?.(updatedDraft);
      } else {
        const useCookie = activeCookie ?? cookie;
        if (!useCookie) return;
        await threatcatService.updateEntity({
          cookie: useCookie,
          id: updated.id,
          diagram_id: updated.diagram_id ?? currentDiagram.id,
          name: updated.name,
          description: updated.description ?? '',
          label: updated.label ?? '',
          type: updated.type,
          boundary_id: updated.boundary_id ?? null,
        });
        // Replacing `diagrams` also re-derives currentDiagram/entitiesMap in AppContext.
        setAppState({
          diagrams: diagrams.map((d, i) =>
            i === currentDiagramIndex ? { ...d, entities: replaceEntity(d.entities, updated) } : d,
          ),
        });
      }

      setSaveSuccess(true);
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setEditingEntity(null);
        setSaveSuccess(false);
      }, 1200);
    } catch (err) {
      if (!isDraft && err instanceof ThreatCatAuthError) {
        // Expired cookie: reconnect, then resume this save.
        disconnect();
        pendingSaveEntityRef.current = updated;
        setConnectOpen(true);
        return;
      }
      setSaveError(err instanceof Error ? err.message : 'Failed to update entity');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEntity = async (updated: Entity) => {
    if (isEditDisabled) return;
    if (!isDraft && !cookie) {
      pendingSaveEntityRef.current = updated;
      setConnectOpen(true);
      return;
    }
    await executeSaveEntity(updated);
  };

  const handleConnected = () => {
    setConnectOpen(false);
    const nextCookie = getCookie();
    const pendingEntity = pendingSaveEntityRef.current;
    pendingSaveEntityRef.current = null;
    if (isEditDisabled) return;
    if (pendingEntity && nextCookie) {
      void executeSaveEntity(pendingEntity, nextCookie);
    }
  };

  const handleConnectClose = () => {
    setConnectOpen(false);
    pendingSaveEntityRef.current = null;
  };

  return (
    <div className="space-y-6">
      <ConnectModal
        open={connectOpen}
        onClose={handleConnectClose}
        onConnected={handleConnected}
      />
      {/* Entities Grid */}
      <Card>
        <CardHeader>
          <CardTitle>System Entities</CardTitle>
          <CardDescription>All entities in the current diagram</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {currentDiagram.entities.map((entity) => {
              const boundaryChain = getEntityBoundaryChain(entity.boundary_id);
              const typeLabel = entityTypeLabels[entity.type] || entity.type;
              const typeColor = entityTypeColors[entity.type] || entityTypeColors.default;
              const entityComments = reviewIndex.getEntityComments(entity.id, currentDiagram.id);

              return (
                <Card key={entity.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1">
                        <CardTitle className="text-base">{entity.name}</CardTitle>
                        <div className="text-xs text-muted-foreground">ID: {entity.id}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${typeColor}`}>
                          {typeLabel}
                        </span>
                        <span title={editDisabledReason ?? undefined} className="inline-flex">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label="Edit entity"
                            data-entity-edit-trigger={entity.id}
                            title={editDisabledReason ?? (isDraft ? 'Edit entity' : 'Edit and sync to ThreatCat')}
                            disabled={isEditDisabled}
                            onClick={() => openEditDialog(entity)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </div>
                    </div>
                    {boundaryChain && (
                      <div className="mt-2">
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {boundaryChain}
                        </span>
                      </div>
                    )}
                  </CardHeader>
                  {(entity.description || entityLabel(entity) || entityComments.length > 0) && (
                    <CardContent className="pt-0 space-y-3">
                      <ReviewCommentsCallout comments={entityComments} />
                      {entity.description && (
                        <p className="text-sm text-muted-foreground">{entity.description}</p>
                      )}
                      {entityLabel(entity) && (
                        <div className="mt-2">
                          <div className="mb-1 text-xs font-semibold text-muted-foreground">Technical information</div>
                          <div className="rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-words text-black dark:text-white">
                            {entityLabel(entity)}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Systems Scope */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Systems Scope</CardTitle>
              <CardDescription>Systems within and outside the analysis scope</CardDescription>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={copyScopeTable} disabled={copied}>
                <Copy className="mr-2 h-4 w-4" />
                {copied ? 'Copied' : 'Copy as Markdown'}
              </Button>
              <span className="sr-only" aria-live="polite">{copied ? 'Scope table copied to clipboard' : ''}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-3 text-sm font-semibold flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" /> Systems in Scope</h3>
              <div className="space-y-2 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                {scopedEntities.inScope.length > 0 ? (
                  <ul className="space-y-2">
                    {scopedEntities.inScope.map((name, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-green-600 dark:text-green-400">•</span>
                        <span>{name}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm italic text-muted-foreground">No systems in scope</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-semibold flex items-center gap-1.5"><XCircle className="h-4 w-4 text-red-600 dark:text-red-400" /> Systems out of Scope</h3>
              <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
                {scopedEntities.outOfScope.length > 0 ? (
                  <ul className="space-y-2">
                    {scopedEntities.outOfScope.map((name, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-red-600 dark:text-red-400">•</span>
                        <span>{name}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm italic text-muted-foreground">No systems out of scope</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <EditEntityDialog
        entity={editingEntity}
        boundaries={currentDiagram.boundaries}
        getBoundaryLabel={getBoundaryLabel}
        isDraft={isDraft}
        isSaving={isSaving}
        saveError={saveError}
        saveSuccess={saveSuccess}
        onCancel={closeEditDialog}
        onSave={(updated) => void handleSaveEntity(updated)}
      />
    </div>
  );
}
